import { LinkType } from 'src/services/common/common.types';

/**
 * Shape of a stored profile link. Candidate (`CPLink`) and contact (`ContactProfileLink`) links
 * both serialise to this pair, so one helper serves both detail headers.
 */
export type ProfileLinkLike = { type: LinkType | string; url?: string | null };

// Only schemes that navigate out to a web page. Anything else (notably `javascript:`) must never
// reach an href.
const SAFE_PROTOCOLS = ['http:', 'https:'];

const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

/**
 * Turn a stored link into an href that is safe to render, or null.
 *
 * Stored URLs are validated with class-validator's `@IsUrl()`, which accepts a bare
 * `linkedin.com/in/jane` with no scheme — as an href that would navigate inside the SPA instead of
 * out to LinkedIn. Migrated JobAdder links bypass DTO validation altogether, so schemeless values
 * are common. Anything that is not http(s) after normalisation is dropped rather than rendered.
 */
export function normalizeExternalUrl(raw?: string | null): string | null {
  const trimmed = raw?.trim();
  if (!trimmed) return null;

  const candidate = HAS_SCHEME.test(trimmed) ? trimmed : `https://${trimmed}`;

  try {
    const parsed = new URL(candidate);
    return SAFE_PROTOCOLS.includes(parsed.protocol) ? parsed.href : null;
  } catch {
    return null;
  }
}

/** Resolve a links collection down to a renderable LinkedIn href, or null when there is none. */
export function linkedinUrlFromLinks(links?: ProfileLinkLike[] | null): string | null {
  const match = links?.find((link) => link.type === LinkType.LINKEDIN);
  return normalizeExternalUrl(match?.url);
}
